import { HttpClient, type AuthTokens, type HttpClientOptions } from './http';
import { RealtimeClient } from './realtime';

export interface ProKanbanClientOptions extends HttpClientOptions {
  /** Origin used for the WebSocket connection; derived from `baseUrl` when omitted. */
  realtimeUrl?: string;
  /** Connect the realtime socket as soon as tokens are set. */
  autoConnectRealtime?: boolean;
}

/**
 * Entry point of the SDK.
 *
 * ```ts
 * const client = new ProKanbanClient({ baseUrl: 'http://localhost:3000/api' });
 * const { tokens } = await client.http.post('auth/login', { email, password });
 * client.setTokens(tokens);
 * const boards = await client.http.get(`projects/${projectId}/boards`);
 * ```
 *
 * After running `npm run sdk:generate`, the fully typed operation surface is
 * available from `@pro-kanban/sdk/generated` and can be attached with
 * `client.attach(new GeneratedApi(client.http))`.
 */
export class ProKanbanClient {
  readonly http: HttpClient;
  readonly realtime: RealtimeClient;
  /** Populated by `attach()` with the generated API surface. */
  api: unknown = null;

  constructor(private readonly options: ProKanbanClientOptions) {
    this.http = new HttpClient({
      ...options,
      onTokensRefreshed: (tokens) => {
        void this.realtime.updateToken(tokens.accessToken);
        options.onTokensRefreshed?.(tokens);
      },
    });
    this.realtime = new RealtimeClient({
      url: options.realtimeUrl ?? deriveOrigin(options.baseUrl),
      token: '',
      // A refused handshake is the socket's only sign that the access token has
      // expired, and nothing else refreshes it: rotation is otherwise driven by
      // a 401, which needs an HTTP call the app may not be about to make.
      refreshToken: async () => {
        if (!(await this.http.refreshSession())) return null;
        return this.http.getAccessToken();
      },
    });
  }

  attach<T>(api: T): T {
    this.api = api;
    return api;
  }

  async setTokens(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'> | null): Promise<void> {
    this.http.setTokens(tokens);
    if (tokens?.accessToken) {
      await this.realtime.updateToken(tokens.accessToken);
      if (this.options.autoConnectRealtime !== false) await this.realtime.connect();
    } else {
      this.realtime.disconnect();
    }
  }

  setLocale(locale: string): void {
    this.http.setLocale(locale);
  }

  /** Uses a private API token instead of an interactive session. */
  useApiToken(token: string): void {
    this.http.setApiToken(token);
  }

  /** URL of a stored file, ready for `<img src>`. */
  fileUrl(fileId: string, options: { thumbnail?: boolean; width?: number; download?: boolean } = {}): string {
    return this.http.url(`files/${fileId}/content`, {
      thumb: options.thumbnail ? 'true' : undefined,
      w: options.width,
      download: options.download ? 'true' : undefined,
    });
  }
}

/** `http://host/api` → `http://host` */
function deriveOrigin(baseUrl: string): string {
  try {
    return new URL(baseUrl).origin;
  } catch {
    return baseUrl.replace(/\/api\/?$/, '');
  }
}
